import * as React from "react";
import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {lazyInject} from "@athena-portal/core";
import {
    ADD_NEW_CARD,
    AnnualPolicyInfo,
    AnnualPolicyPremiumTypeEnum,
    FerryPaymentHistoryResponse,
    ICacheManager,
    InstalmentsInfo,
    INTERACTION_PROCESS_MTA_ADD_PAY_OFF_BALANCE,
    INTERACTION_PROCESS_MTA_CHANGE_DIRECT_DEBIT,
    INTERACTION_PROCESS_MTA_CHANGE_PAYMENT_DAY,
    Mandate,
    Party,
    PaymentCard,
    Policy,
    POLICY_STATUS_MTC,
    POLICY_STATUS_RENEWAL,
    PolicyPropertyEnum,
    PREMIUM_TYPE_MONTHLY,
    ProductsEnum,
    Renewal,
    Types,
    WOLF_MISSED_PAYMENT,
} from "@ferry/journey-services";
import {PluginTemplates} from "../../../../../../utils/PluginTemplates";
import FalconHeader from "../../../../../../components/navigation/FalconHeader";
import {AutomaticRenewalCheckboxSection} from "./AutomaticRenewalCheckboxSection";
import {Navigate} from "react-router";
import {StorageSuccessModal} from "../../../../../../components/modals/StorageSuccessModal";
import {
    findMainAnnualPaymentCardByMainCardId,
    getAmountWithDecimalDigits,
    renderCardTypeName
} from "../../../../../../CommonFunctions";
import * as moment from "moment/moment";
import {
    EditCarMtaSuccessModalWordingGenerator
} from "../../../../annual/motor/yourCarsPage/modal/EditCarMtaSuccessModalWordingGenerator";
import {AnnualPaymentCardsSection} from "./AnnualPaymentCardsSection";
import {OverdueInstalmentBanner} from "../../../../../../components/banners/OverdueInstalmentBanner";
import {PaymentDaySection} from "../PaymentDaySection";
import {DirectDebitSection} from "./DirectDebitSection";
import {getLatestActivePaymentMandate, getLatestActiveRefundMandate} from "../../../../../../utils/MandateSelection";
import {DirectDebitRenewalPaymentPlanOutput} from "../DirectDebitRenewalPaymentPlanOutput";
import {pushEventToAdobeDataLayerTimeout} from "../../../../../../utils/AdobeDataLayerHelper";
import {PaymentHistoryTable} from "../PaymentHistoryTable";
import {Types as CustomerPortalTypes} from "../../../../../../Types";
import type {ICustomerPortalService} from "../../../../../../services/ICustomerPortalService";
import {SetUpDirectDebitBanner} from "../../../../../../components/banners/SetUpDirectDebitBanner";
import {CancellationBanner} from "../../../../../../components/banners/CancellationBanner";
import {RenewalMissedPaymentBanner} from "../../../../../../components/banners/RenewalMissedPaymentBanner";
import {getPrettifiedInteractionBrandName, FalconNavigatorHeader, Footer} from "@ferry/portal-core";

interface IParams {
    policyNumber: string;
    [key: string]: string | undefined
}

interface IState {
    policy?: Policy;
    party?: Party;
    policyLoaded: boolean;
    instalmentsInfo?: InstalmentsInfo;
    instalmentsInfoLoaded: boolean;
    partyLoaded: boolean;
    renewedPolicy: boolean;
    showMissedPaymentBanner: boolean;
    mainCardId?: string;
    renewalProposal?: Renewal;
    annualToMonthlyRenewal: boolean;
    haveMandates: boolean;
    mandate?: Mandate;
    refundMandate?: Mandate;
    reuseCard: boolean;
    automaticRenewal: boolean;
    performRenewal: boolean;
    paymentHistory?: FerryPaymentHistoryResponse;
    currentPeriodPaymentHistory?: FerryPaymentHistoryResponse;
    pastPeriodPaymentHistory?: FerryPaymentHistoryResponse;
    renewalProposalLoaded: boolean;
}

@authorize
export class AnnualPaymentDetailsPagePlain extends React.Component<IWithRouterProps<IParams>, IState> {

    private pluginTemplates = new PluginTemplates();
    private referrerJourney: string | null;
    private showSuccessPopup: string | null;
    private title: string;
    private modalContent: React.ReactNode;
    private anythingElseTitle: any;
    private explanationAnythingElseWording: any;

    @lazyInject(CustomerPortalTypes.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    @lazyInject(Types.CacheManager)
    private cacheManager: ICacheManager;

    private pushAdobeDataLayerTimeout: any;

    constructor(props: IWithRouterProps<IParams>) {
        super(props);

        this.state = {
            policyLoaded: false,
            instalmentsInfoLoaded: false,
            partyLoaded: false,
            renewedPolicy: false,
            showMissedPaymentBanner: false,
            annualToMonthlyRenewal: false,
            haveMandates: false,
            reuseCard: false,
            automaticRenewal: false,
            performRenewal: false,
            renewalProposalLoaded: false,
        };

        this.pluginTemplates.setPageTitle("Payment details");
    }

    public componentDidMount(): void {
        this.cacheManager.clearCache("getPolicy");
        this.cacheManager.clearCache("getProfile");

        const policyNumber: string = this.props.params.policyNumber;

        this.customerPortalService.getPolicy(policyNumber).subscribe((policy: Policy) => {
            const renewedPolicy: boolean = policy.policyHistoryList.filter(ph => ph.status === POLICY_STATUS_RENEWAL).length > 0;

            const annualPolicyInfo: AnnualPolicyInfo | undefined = policy.annualPolicyInfo || undefined;
            const mandate: Mandate | undefined = getLatestActivePaymentMandate(annualPolicyInfo?.mandates);
            const refundMandate: Mandate | undefined = getLatestActiveRefundMandate(annualPolicyInfo?.mandates);

            policy.policyProperties.forEach(property => {
                if (property.key === PolicyPropertyEnum.POLICY_DEBT) {
                    this.setState({ showMissedPaymentBanner: property.value === "true" });
                } else if (property.key === PolicyPropertyEnum.MAIN_ANNUAL_PAYMENT_CARD) {
                    this.setState({ mainCardId: property.value });
                } else if (property.key === PolicyPropertyEnum.REUSE_MAIN_CARD) {
                    this.setState({ reuseCard: property.value === "true" });
                } else if (property.key === PolicyPropertyEnum.PERFORM_RENEWAL) {
                    this.setState({ performRenewal: property.value === "true" });
                } else if (property.key === PolicyPropertyEnum.AUTOMATIC_RENEWAL) {
                    this.setState({ automaticRenewal: property.value === "true" });
                }
            });

            if (policy.interaction?.factors?.premiumType === AnnualPolicyPremiumTypeEnum.ANNUAL) {
                this.setState({ renewalProposalLoaded: false });

                this.customerPortalService.getRenewalProposal(policyNumber).subscribe((renewalProposal: Renewal) => {
                    const annualToMonthlyRenewal: boolean = renewalProposal?.factors.premiumType === AnnualPolicyPremiumTypeEnum.MONTHLY;

                    this.setState({
                        renewalProposal,
                        annualToMonthlyRenewal,
                        renewalProposalLoaded: true,
                    });
                });
            } else {
                this.setState({
                    renewalProposalLoaded: true,
                });
            }

            this.setState({
                policy,
                renewedPolicy,
                policyLoaded: true,
                haveMandates: !!mandate,
                mandate,
                refundMandate,
            });

            if (renewedPolicy) {
                this.customerPortalService
                    .getCurrentPeriodPaymentHistory(policyNumber)
                    .subscribe((currentPeriodPaymentHistory: FerryPaymentHistoryResponse) => {
                        this.setState({
                            currentPeriodPaymentHistory: currentPeriodPaymentHistory,
                        });
                    });
                this.customerPortalService.getPastPeriodPaymentHistory(policyNumber).subscribe((pastPeriodPaymentHistory: FerryPaymentHistoryResponse) => {
                    this.setState({
                        pastPeriodPaymentHistory: pastPeriodPaymentHistory,
                    });
                });
            } else {
                this.customerPortalService.getPaymentHistory(policyNumber).subscribe((paymentHistory: FerryPaymentHistoryResponse) => {
                    this.setState({
                        paymentHistory,
                    });
                });
            }
        });

        this.customerPortalService.getInstalmentsInfo(policyNumber, false).subscribe((instalmentsInfo: InstalmentsInfo) => {
            this.setState({
                instalmentsInfo,
                instalmentsInfoLoaded: true,
            });
        });

        this.customerPortalService.getProfile().subscribe((output: Party) => {
            this.setState({
                party: output,
                partyLoaded: true,
            });
        });

        const successModalRef = window.sessionStorage.getItem("referrerJourney");
        if (this.showSuccessPopup) {
            if (successModalRef?.startsWith("mta")) {
                this.pushAdobeDataLayerTimeout = pushEventToAdobeDataLayerTimeout(2000, "mta_completed");
            }
        }
    }

    public componentWillUnmount() {
        clearTimeout(this.pushAdobeDataLayerTimeout);
    }

    public render() {
        const loading: boolean = !(this.state.policyLoaded && this.state.partyLoaded && this.state.renewalProposalLoaded);

        const { policy } = this.state;

        this.referrerJourney = window.sessionStorage.getItem("referrerJourney");
        this.showSuccessPopup = window.sessionStorage.getItem("showPopup");

        const lastPolicyInteraction = policy ? policy.interaction : null;
        if (!loading && policy) {
            if (this.referrerJourney === ADD_NEW_CARD) {
                this.setNewCardPopup();
            } else if (this.referrerJourney === INTERACTION_PROCESS_MTA_ADD_PAY_OFF_BALANCE) {
                this.setPayOffBalancePopup();
            } else if (this.referrerJourney === INTERACTION_PROCESS_MTA_CHANGE_PAYMENT_DAY) {
                this.setPaymentDayChangePopup();
            } else if (this.referrerJourney === INTERACTION_PROCESS_MTA_CHANGE_DIRECT_DEBIT) {
                this.setDirectDebitSetupPopup();
            } else if (this.referrerJourney === WOLF_MISSED_PAYMENT) {
                this.setMissedRenewalPaymentPopup();
            } else {
                window.sessionStorage.removeItem("showPopup");
                window.sessionStorage.removeItem("referrerJourney");
            }

            if (policy.annualPolicyInfo.premiumType === PREMIUM_TYPE_MONTHLY || policy.annualPolicyInfo.monthlyPaymentInCurrentPeriod) {
                return <Navigate to={`/dashboard/monthlyPaymentHistory/${this.props.params.policyNumber}`}/>
            }

            return this.renderPageContent(policy, lastPolicyInteraction);
        } else {
            return <Overlay show={true} />;
        }
    }

    private renderPageContent(policy: Policy, lastPolicyInteraction: any) {
        const policyIsActive = moment(policy?.endDate).isSameOrAfter(moment()) && policy?.status !== "MTC";
        const backLinkUrl: string = `/dashboard/annualMotor/${this.props.params.policyNumber}`;

        const annualPolicyInfo = policy?.annualPolicyInfo;
        const policyCancelled = policy?.status === POLICY_STATUS_MTC;

        return (
            <div className="dashboard payment-details">
                {this.showSuccessPopup && (
                    <StorageSuccessModal
                        title={this.title}
                        explanationWording={this.modalContent}
                        anythingElseTitle={this.anythingElseTitle}
                        explanationAnythingElseWording={this.explanationAnythingElseWording}
                    />
                )}

                <FalconHeader />

                <div className="plugin">
                    <FalconNavigatorHeader back={backLinkUrl} title="Payment details" />

                    {annualPolicyInfo?.overdueMonthlyInvoiceAmount != null && +annualPolicyInfo.overdueMonthlyInvoiceAmount > 0 && (
                        <OverdueInstalmentBanner
                            amount={annualPolicyInfo.overdueMonthlyInvoiceAmount}
                            unpaidInstallmentIdToAmount={annualPolicyInfo.unpaidInstallmentIdToAmount}
                            policyNumber={lastPolicyInteraction.agreementNumber}
                            product={ProductsEnum.WOLF_MOTOR}
                            reuseCard={this.state.reuseCard}
                        />
                    )}

                    {policyCancelled && <CancellationBanner policy={policy} process={lastPolicyInteraction.process}/>}
                    {!policyCancelled && <RenewalMissedPaymentBanner policy={policy} reuseCard={this.state.reuseCard} />}

                    {this.state.policy?.annualPolicyInfo?.isMandateRequiredForRefund && <SetUpDirectDebitBanner policyNumber={this.props.params.policyNumber}/>}

                    <div className="panel bg-white overflow-hidden p-0 font-weight-extra-light">
                        <div className="payment-details-table">
                            <div className="padding-32">
                                <AnnualPaymentCardsSection
                                    policyNumber={this.props.params.policyNumber}
                                    payment="annual"
                                    mainCardId={this.state.mainCardId}
                                    annualPolicyInfo={annualPolicyInfo}
                                    hasRefundMandate={!!this.state.refundMandate}
                                />
                            </div>

                            {this.state.policyLoaded && this.state.refundMandate && (
                                <div className="padding-32">
                                    <DirectDebitSection haveActiveMandate={true}
                                                        policyNumber={this.props.params.policyNumber}
                                                        interaction={lastPolicyInteraction}
                                                        mandate={this.state.refundMandate}
                                                        hasRefundMandate={true}/>
                                </div>
                            )}

                            {!this.state.renewedPolicy && this.state.paymentHistory && (
                                <div className="padding-32">
                                    <div className="blue-line" />
                                    <PaymentHistoryTable paymentHistory={this.state.paymentHistory} policyNumber={this.props.params.policyNumber} />
                                </div>
                            )}
                            {this.state.renewedPolicy && (
                                <>
                                    <div className="padding-32">
                                        {this.state.currentPeriodPaymentHistory && (
                                            <PaymentHistoryTable
                                                title="Existing policy payment history"
                                                paymentHistory={this.state.currentPeriodPaymentHistory}
                                                policyNumber={this.props.params.policyNumber}
                                            />
                                        )}
                                    </div>
                                    <div className="padding-32">
                                        {this.state.pastPeriodPaymentHistory && (
                                            <PaymentHistoryTable
                                                title="Old policies payment history"
                                                paymentHistory={this.state.pastPeriodPaymentHistory}
                                                policyNumber={this.props.params.policyNumber}
                                            />
                                        )}
                                    </div>
                                </>
                            )}

                            {this.state.policyLoaded && this.state.annualToMonthlyRenewal && this.state.renewalProposal && (
                                <div className="padding-32">
                                    <PaymentDaySection
                                        policyCancelled={this.state.policy?.status === POLICY_STATUS_MTC}
                                        interaction={this.state.renewalProposal}
                                        paymentType="annual-product-monthly-payments"
                                        paymentDay={this.state.renewalProposal.paymentDetails?.directDebitDetails?.paymentDay}
                                        canChangePaymentDate={true}
                                        renewalPaymentFailed={false}
                                        annualToMonthly={true}
                                    />
                                </div>
                            )}

                            {this.state.policyLoaded && this.state.annualToMonthlyRenewal && this.state.mandate && this.state.renewalProposal && (
                                <div className="padding-32">
                                    <DirectDebitSection haveActiveMandate={this.state.haveMandates}
                                                        policyNumber={this.props.params.policyNumber}
                                                        interaction={this.state.renewalProposal}
                                                        mandate={this.state.mandate}
                                                        annualToMonthly={true}/>
                                </div>
                            )}

                            {this.state.policyLoaded && this.state.annualToMonthlyRenewal && this.state.renewalProposal && (
                                <div className="padding-32">
                                    <div id="paymentScheduleTable">
                                        <DirectDebitRenewalPaymentPlanOutput interaction={this.state.renewalProposal} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {policyIsActive && (
                        <AutomaticRenewalCheckboxSection
                            navigate={this.props.navigate}
                            activeAnnualPolicy={policy}
                            automaticRenewal={this.state.automaticRenewal}
                            performRenewal={this.state.performRenewal}
                            twoDaysGracePeriod={annualPolicyInfo.inGracePeriod}
                            renewalProposal={this.state.renewalProposal}
                        />
                    )}
                    {policy && this.pluginTemplates.renderBackLink("Back", false, backLinkUrl)}
                </div>
                <Footer />
            </div>
        );
    }

    private setNewCardPopup(): void {
        const paymentCard: PaymentCard | null = findMainAnnualPaymentCardByMainCardId(this.state.party!, this.state.mainCardId!);
        if (paymentCard) {
            this.title = "All done!";
            this.modalContent = (
                <p className="font-weight-bold">
                    You have a new payment method, {renderCardTypeName(paymentCard).toUpperCase()} card ending in{" "}
                    {paymentCard.cardNumberObfuscated.split("******")[1]}
                </p>
            );
            this.anythingElseTitle = "";
            this.explanationAnythingElseWording = "";
        }
    }

    private setPayOffBalancePopup(): void {
        this.title = "Payment was successful";
        this.modalContent = (
            <p>{getAmountWithDecimalDigits(this.state.policy!.interaction?.factors.payOffPremium)} has been taken out from your account for paying off your car insurance loan</p>
        );
        this.anythingElseTitle = EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalTitle();
        this.explanationAnythingElseWording = EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalBody();
    }

    private setMissedRenewalPaymentPopup(): void {
        const paymentAmount = window.sessionStorage.getItem("paymentAmount");
        const paymentCard: PaymentCard | null = findMainAnnualPaymentCardByMainCardId(this.state.party!, this.state.mainCardId!);
        const brandName = getPrettifiedInteractionBrandName(this.state.policy!.interaction?.brandName);
        this.title = "Payment was successful";
        this.modalContent =
            <p>
                <b>{paymentAmount}</b> has been debited from your card ending {paymentCard?.cardNumberObfuscated.split('******')[1]} to
                renew your next year's policy.
            </p>;
        this.anythingElseTitle = "Please allow a couple of minutes for the payment to be updated in your account";
        this.explanationAnythingElseWording = `Thanks for choosing ${brandName} for another year!`;
    }

    private setPaymentDayChangePopup(): void {
        this.title = "All done!";
        this.modalContent = (
            <div>
                <p>
                    Your new Direct Debit details have been updated along with your Direct Debit reference. You'll be able to see a new payment plan in your
                    'Documents' tile shortly.
                </p>
            </div>
        );
    }

    private setDirectDebitSetupPopup(): void {
        this.title = "All done!";
        this.modalContent = (
            <div>
                <p>
                    Your new Direct Debit details have been updated along with your Direct Debit reference. You'll be able to see a new payment plan in your
                    'Documents' tile shortly.
                </p>
            </div>
        );
    }
}


export const AnnualPaymentDetailsPage = withRouter<{}, IParams>(AnnualPaymentDetailsPagePlain)
